
import React, { useState, useEffect } from 'react';
import { Card } from "@/components/ui/card";
import { UserCircle, CreditCard, Bell, Shield, Building2, Palette } from "lucide-react";
import ResponsiveTabs from "@/components/ui/responsive-tabs";
import { useIsMobile } from "@/hooks/use-mobile";
import AccountTabContent from '@/components/settings/account/AccountTabContent';
import ProfileTabContent from "@/components/settings/tabs/ProfileTabContent";
import BrandingTabContent from "@/components/settings/tabs/BrandingTabContent";
import SaveChangesButton from "@/components/settings/SaveChangesButton";
import BusinessSettingsLoading from "@/components/settings/BusinessSettingsLoading";
import { useBusinessSettings } from "@/hooks/useBusinessSettings";

const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState("profile");
  const isMobile = useIsMobile();
  
  const { 
    isLoading, 
    businessData, 
    isSaving, 
    isDirty,
    updateBusinessData,
    updateMultipleFields,
    handleSave 
  } = useBusinessSettings();

  // Create the tabs configuration
  const tabItems = [
    {
      id: "profile",
      icon: <Building2 className="h-4 w-4" />,
      label: "Business Profile",
      mobileLabel: "Profile",
    },
    {
      id: "branding",
      icon: <Palette className="h-4 w-4" />,
      label: "Branding & Marketing",
      mobileLabel: "Branding",
    },
    {
      id: "account",
      icon: <Shield className="h-4 w-4" />,
      label: "Account",
      mobileLabel: "Account",
    }
  ];

  if (isLoading) {
    return <BusinessSettingsLoading />;
  }

  const tabs = tabItems.map(item => ({
    value: item.id,
    label: (
      <div className="flex items-center gap-1">
        {item.icon}
        <span className="hidden sm:inline">{item.label}</span>
        <span className="sm:hidden">{item.mobileLabel}</span>
      </div>
    ),
    content: (
      <TabContent 
        id={item.id} 
        title={item.label}
        isMobile={isMobile}
        businessData={businessData}
        updateBusinessData={updateBusinessData}
        updateMultipleFields={updateMultipleFields}
      />
    )
  }));

  return (
    <div className="container mx-auto py-6 max-w-4xl animate-fade-in">
      <h1 className="text-3xl font-bold mb-6">Settings</h1>
      
      <ResponsiveTabs
        defaultValue="profile"
        tabs={tabs}
        className="w-full"
        onChange={setActiveTab}
      />
      
      {(activeTab === "profile" || activeTab === "branding") && (
        <SaveChangesButton 
          isSaving={isSaving} 
          onSave={handleSave}
          isDirty={isDirty}
        />
      )}
    </div>
  );
};

// Extract tab content to its own component for better organization
const TabContent = ({ 
  id, 
  title, 
  isMobile, 
  businessData,
  updateBusinessData,
  updateMultipleFields
}: { 
  id: string; 
  title: string; 
  isMobile: boolean;
  businessData?: any;
  updateBusinessData?: (key: string, value: any) => void;
  updateMultipleFields?: (updates: Record<string, any>) => void;
}) => {
  
  const renderContent = () => {
    switch (id) {
      case "profile":
        return (
          <ProfileTabContent 
            businessData={businessData} 
            updateBusinessData={updateBusinessData}
            updateMultipleFields={updateMultipleFields}
          />
        );

      case "branding":
        return (
          <BrandingTabContent 
            businessData={businessData} 
            updateBusinessData={updateBusinessData} 
          />
        );

      case "account":
        return <AccountTabContent />;

      default:
        return <p>Settings coming soon</p>;
    }
  };

  return (
    <Card className={`p-4 sm:p-6 ${isMobile ? 'overflow-visible' : ''}`}>
      {renderContent()}
    </Card>
  );
};

export default SettingsPage;
